import { addDays, addYears, format, isBefore, subDays, subYears } from 'date-fns';

import { Box, DateInput, DatePicker, FormField, FormHelp, Label, TextButton } from '@zillow/constellation-10';

import type { Lease, Section } from '../../../models/index';
import { LEASE_TERMS } from '../../../constants';
import LeaseTermsListBox from './LeaseTerms/LeaseTermsListBox';
import { DATE_PICKER_FORMAT } from '../../../constants/stringConstants';
import { ValidationType } from '../../../utils/validators';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: Record<string, Date | string | boolean>) => void;
  lease: Lease;
};

const END_DATE_ERROR_MESSAGE = 'Enter a valid date that is after the start date';

export default function LeaseTerms_v4({ section, setSectionValues, lease }: Props): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const endDateValidator = (value: any): boolean => {
    const startDate = section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE);
    const oneDayAfterStart = addDays(startDate, 1);

    if (startDate && value && isBefore(value, oneDayAfterStart)) {
      return false;
    }
    return true;
  };

  // Override the validators on every render to ensure they're always set correctly.
  // We have set the validator within the API to be "TermStartDateValidator" and "TermEndDateValidator", but since we don't want to mess with that, we're overriding it here.
  // This is necessary because SectionWrapper clones the section multiple times
  const startDateField = section.getField(LEASE_TERMS.FIELD_NAME.START_DATE);
  if (startDateField) {
    const currentValue = startDateField.enteredValue;
    startDateField.validation = ValidationType.TermsStartDateExtendedRangeValidator;
    if (currentValue) {
      startDateField.setEnteredValue(currentValue, true);
    }
  }

  const endDateField = section.getField(LEASE_TERMS.FIELD_NAME.END_DATE);
  if (endDateField) {
    const currentValue = endDateField.enteredValue;
    endDateField.validation = ValidationType.TermsStartDateExtendedRangeValidator;
    endDateField.customValidator = endDateValidator;
    if (currentValue) {
      endDateField.setEnteredValue(currentValue, true);
    }
  }

  function isSelected(termFieldName: string): boolean {
    return section.getFieldValue(termFieldName) || false;
  }

  function getStartDate(): string {
    return section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE);
  }

  function getEndDate(): string {
    return section.getFieldValue(LEASE_TERMS.FIELD_NAME.END_DATE);
  }

  // Start Date handlers
  function onStartDateChange(formattedDate: string): void {
    setSectionValues({
      [LEASE_TERMS.FIELD_NAME.START_DATE]: formattedDate,
      [LEASE_TERMS.FIELD_NAME.END_DATE]: getEndDate(),
    });
  }

  // End Date handlers
  function onEndDateChange(formattedDate: string): void {
    setSectionValues({
      [LEASE_TERMS.FIELD_NAME.END_DATE]: formattedDate,
    });
    validateEndDate();
  }

  function validateEndDate(): void {
    const field = section.getField(LEASE_TERMS.FIELD_NAME.END_DATE);
    field.setErrorMessage(END_DATE_ERROR_MESSAGE);
  }

  function onSetOneYearTermClick(): void {
    const startDate = getStartDate();
    const formattedOneYearLaterEndDate = format(subDays(addYears(startDate, 1), 1), 'MMMM d, yyyy');

    setSectionValues({ [LEASE_TERMS.FIELD_NAME.END_DATE]: formattedOneYearLaterEndDate });
  }

  function setOneYearTermLabel(): string {
    if (
      section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError ||
      !section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE)
    ) {
      return 'Set to 1 year term';
    }
    const startDate = getStartDate();
    const formattedOneYearLaterEndDate = format(subDays(addYears(startDate, 1), 1), 'MMMM d, yyyy');

    return `Set to 1 year term (${formattedOneYearLaterEndDate})`;
  }

  return (
    <>
      <LeaseTermsListBox
        section={section}
        setSectionValues={setSectionValues}
        lease={lease}
        legendText="What are your lease terms?"
      />
      {(isSelected(LEASE_TERMS.FIELD_NAME.IS_MONTH_TO_MONTH) || isSelected(LEASE_TERMS.FIELD_NAME.IS_FIXED)) && (
        <Box css={{ marginBottom: 'layout.tight' }}>
          <FormField
            required
            css={{ marginBottom: 'layout.tighter' }}
            label={<Label>{LEASE_TERMS.LABEL.START_DATE}</Label>}
            error={section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError}
            control={
              <DatePicker
                dateFormat={DATE_PICKER_FORMAT}
                value={getStartDate()}
                onValueChange={(value) => {
                  const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
                  onStartDateChange(formatted);
                }}
                minDate={subYears(new Date(), 6)}
                maxDate={addYears(new Date(), 5)}
                closeModalOnDateSelect
                input={<DateInput />}
              />
            }
            description={
              section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError && (
                <FormHelp>{section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).getErrorMessage()}</FormHelp>
              )
            }
          />
          {!isSelected(LEASE_TERMS.FIELD_NAME.IS_MONTH_TO_MONTH) && (
            <Box css={{ marginTop: 'layout.tight' }}>
              <FormField
                required
                css={{ marginBottom: 'layout.tighter' }}
                label={<Label>{LEASE_TERMS.LABEL.END_DATE}</Label>}
                error={section.getField(LEASE_TERMS.FIELD_NAME.END_DATE).hasError}
                control={
                  <DatePicker
                    dateFormat={DATE_PICKER_FORMAT}
                    value={getEndDate()}
                    onValueChange={(value) => {
                      const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
                      onEndDateChange(formatted);
                    }}
                    minDate={subYears(new Date(), 6)}
                    maxDate={addYears(new Date(), 5)}
                    closeModalOnDateSelect
                    input={<DateInput />}
                  />
                }
                description={
                  section.getField(LEASE_TERMS.FIELD_NAME.END_DATE).hasError && (
                    <FormHelp>{section.getField(LEASE_TERMS.FIELD_NAME.END_DATE).getErrorMessage()}</FormHelp>
                  )
                }
              />
              <TextButton
                disabled={
                  section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError ||
                  !section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE)
                }
                onClick={onSetOneYearTermClick}
              >
                {setOneYearTermLabel()}
              </TextButton>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
